import React, { useEffect, useState } from "react";
import { BookOpen, Lock, UserPlus, KeyRound } from "lucide-react";
import { getSession, login, register, resetPassword } from "@/lib/auth";

const emptyForm = {
  name: "",
  username: "",
  password: "",
};

export default function AuthGate({ children }) {
  const [session, setSession] = useState(() => getSession());
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sync = () => setSession(getSession());
    window.addEventListener("liarthur-auth-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("liarthur-auth-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (session) return children;

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setNotice("");
    setForm(emptyForm);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);

    try {
      if (mode === "login") {
        await login(form);
      } else if (mode === "register") {
        await register(form);
      } else {
        await resetPassword(form);
        setNotice("Şifre güncellendi. Giriş yapıldı.");
      }
    } catch (err) {
      setError(err.message || "İşlem tamamlanamadı.");
    } finally {
      setLoading(false);
    }
  };

  const title =
    mode === "login"
      ? "Giriş yap"
      : mode === "register"
      ? "Kaydol"
      : "Şifremi unuttum";

  const helper =
    mode === "login"
      ? "Çalışmaya devam etmek için kullanıcı adınız ve şifrenizle giriş yapın."
      : mode === "register"
      ? "Bu cihazda kullanacağınız bir öğrenci hesabı oluşturun."
      : "Kullanıcı adınızı girip yeni şifrenizi belirleyin.";

  return (
    <div className="min-h-screen bg-paper bg-grain flex items-center justify-center px-4 py-10">
      <main className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-11 w-11 rounded-full bg-[#2e4733] flex items-center justify-center">
            <BookOpen size={18} className="text-[#f5f6f2]" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-serif-fact text-2xl text-[#1c2723] leading-tight">
              Liarthur
            </div>
            <div className="label-eyebrow text-[#a3aba6]">Tekrarla Öğren</div>
          </div>
        </div>

        <section className="bg-white border border-[#e8eae3] rounded-2xl p-7 fact-card-shadow">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-full bg-[#f5f6f2] border border-[#e8eae3] flex items-center justify-center">
              {mode === "register" ? (
                <UserPlus size={16} className="text-[#2e4733]" />
              ) : mode === "forgot" ? (
                <KeyRound size={16} className="text-[#2e4733]" />
              ) : (
                <Lock size={16} className="text-[#2e4733]" />
              )}
            </div>
            <div>
              <h1 className="font-serif-fact text-2xl text-[#1c2723]">
                {title}
              </h1>
              <p className="text-sm text-[#5c635e] mt-1">{helper}</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input
                value={form.name}
                onChange={update("name")}
                placeholder="Ad soyad"
                className="w-full h-11 rounded-xl border border-[#e8eae3] px-4 text-sm outline-none focus:border-[#4a6741]"
              />
            )}
            <input
              value={form.username}
              onChange={update("username")}
              placeholder="Kullanıcı adı"
              autoFocus
              className="w-full h-11 rounded-xl border border-[#e8eae3] px-4 text-sm outline-none focus:border-[#4a6741]"
            />
            <input
              value={form.password}
              onChange={update("password")}
              type="password"
              placeholder={mode === "forgot" ? "Yeni şifre" : "Şifre"}
              className="w-full h-11 rounded-xl border border-[#e8eae3] px-4 text-sm outline-none focus:border-[#4a6741]"
            />

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-xl bg-[#4a6741]/10 border border-[#4a6741]/20 px-4 py-3 text-sm text-[#2e4733]">
                {notice}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#2e4733] text-[#f5f6f2] hover:bg-[#1c2723] transition-colors text-sm font-medium"
            >
              {loading
                ? "Bekleyin..."
                : mode === "login"
                ? "Giriş yap"
                : mode === "register"
                ? "Kaydol ve giriş yap"
                : "Şifreyi güncelle"}
            </button>
          </form>

          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            {mode !== "login" && (
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="h-9 px-3 rounded-full text-sm text-[#5c635e] hover:bg-[#f5f6f2]"
              >
                Girişe dön
              </button>
            )}
            {mode !== "register" && (
              <button
                type="button"
                onClick={() => switchMode("register")}
                className="h-9 px-3 rounded-full text-sm text-[#5c635e] hover:bg-[#f5f6f2]"
              >
                Kaydol
              </button>
            )}
            {mode !== "forgot" && (
              <button
                type="button"
                onClick={() => switchMode("forgot")}
                className="h-9 px-3 rounded-full text-sm text-[#5c635e] hover:bg-[#f5f6f2]"
              >
                Şifremi unuttum
              </button>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
